import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import axios from 'axios'
import toast from 'react-hot-toast'
import { ArrowLeftIcon, PencilIcon, TrashIcon, EyeIcon, HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline'
import { formatMarkdown } from '../utils/markdownParser'

interface KnowledgeArticle {
  id: string
  title: string
  content: string
  excerpt?: string
  scope: 'internal' | 'client_specific' | 'global'
  status: 'draft' | 'published' | 'archived'
  viewCount: number
  helpfulCount: number
  notHelpfulCount: number
  createdAt: string
  updatedAt: string
  category?: {
    id: string
    name: string
    description?: string
  }
  author: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  clientOrg?: {
    id: string
    name: string
    domain: string
  }
}

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [article, setArticle] = useState<KnowledgeArticle | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    categoryId: '',
    scope: 'global' as 'internal' | 'client_specific' | 'global',
    status: 'draft' as 'draft' | 'published' | 'archived'
  })
  const [isSaving, setIsSaving] = useState(false)

  const isStaff = user?.roles?.some(role => ['administrator', 'support_staff', 'manager'].includes(role))

  useEffect(() => {
    if (id) {
      loadArticle()
    }
  }, [id])

  const loadArticle = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(`/api/knowledge/${id}`)
      
      if (response.data.success) {
        setArticle(response.data.data)
      } else {
        toast.error('Failed to load article')
        navigate('/knowledge')
      }
    } catch (error: any) {
      console.error('Error loading article:', error)
      toast.error(error.response?.data?.error || 'Failed to load article')
      navigate('/knowledge')
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = () => {
    if (!article) return
    
    setEditForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || '',
      categoryId: article.category?.id || '',
      scope: article.scope,
      status: article.status
    })
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!article) return

    try {
      setIsSaving(true)
      const response = await axios.put(`/api/knowledge/${article.id}`, editForm)
      
      if (response.data.success) {
        toast.success('Article updated successfully')
        setIsEditing(false)
        loadArticle() // Reload to get updated data
      } else {
        toast.error(response.data.error || 'Failed to update article')
      }
    } catch (error: any) {
      console.error('Error updating article:', error)
      toast.error(error.response?.data?.error || 'Failed to update article')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditForm({
      title: '',
      content: '',
      excerpt: '',
      categoryId: '',
      scope: 'global',
      status: 'draft'
    })
  }

  const handleDelete = async () => {
    if (!article) return

    try {
      setIsDeleting(true)
      const response = await axios.delete(`/api/knowledge/${article.id}`)
      
      if (response.data.success) {
        toast.success('Article deleted successfully')
        navigate('/knowledge')
      } else {
        toast.error(response.data.error || 'Failed to delete article')
      }
    } catch (error: any) {
      console.error('Error deleting article:', error)
      toast.error(error.response?.data?.error || 'Failed to delete article')
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleRate = async (helpful: boolean) => {
    if (!article) return

    try {
      const response = await axios.post(`/api/knowledge/${article.id}/rate`, { helpful })
      
      if (response.data.success) {
        // Update local state
        setArticle(prev => prev ? {
          ...prev,
          helpfulCount: helpful ? prev.helpfulCount + 1 : prev.helpfulCount,
          notHelpfulCount: !helpful ? prev.notHelpfulCount + 1 : prev.notHelpfulCount
        } : null)
        toast.success(helpful ? 'Marked as helpful!' : 'Marked as not helpful')
      }
    } catch (error: any) {
      console.error('Error rating article:', error)
      toast.error('Failed to rate article')
    }
  }

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case 'internal':
        return 'Internal'
      case 'client_specific':
        return 'Client-Specific'
      case 'global':
        return 'Global'
      default:
        return scope
    }
  }

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'internal':
        return 'bg-purple-100 text-purple-800'
      case 'client_specific':
        return 'bg-blue-100 text-blue-800'
      case 'global':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'archived':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    )
  }

  if (!article) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Article not found</p>
          <button
            onClick={() => navigate('/knowledge')}
            className="btn-primary mt-4"
          >
            Back to Knowledge Base
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/knowledge')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Knowledge Base
        </button>
        
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-semibold text-gray-900 mb-2">{article.title}</h1>
            
            {/* Article Meta */}
            <div className="flex items-center space-x-3 mb-4">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getScopeColor(article.scope)}`}>
                {getScopeLabel(article.scope)}
              </span>
              {isStaff && (
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(article.status)}`}>
                  {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                </span>
              )}
              {article.category && (
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                  {article.category.name}
                </span>
              )}
            </div>

            {/* Article Stats */}
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span className="flex items-center">
                <EyeIcon className="h-4 w-4 mr-1" />
                {article.viewCount} views
              </span>
              <span className="flex items-center">
                <HandThumbUpIcon className="h-4 w-4 mr-1" />
                {article.helpfulCount} helpful
              </span>
              <span className="flex items-center">
                <HandThumbDownIcon className="h-4 w-4 mr-1" />
                {article.notHelpfulCount} not helpful
              </span>
              <span>By {article.author.firstName} {article.author.lastName}</span>
              <span>Updated {formatDate(article.updatedAt)}</span>
            </div>
          </div>

          {/* Staff Actions */}
          {isStaff && !isEditing && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleEdit}
                className="btn-secondary inline-flex items-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn-secondary text-red-600 hover:text-red-700 inline-flex items-center"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </button>
            </div>
          )}
          {isStaff && isEditing && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                className="btn-secondary inline-flex items-center"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary inline-flex items-center"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Article Content - Edit Mode */}
      {isEditing ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="input-field w-full"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
              <textarea
                value={editForm.content}
                onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                className="input-field w-full"
                rows={10}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
              <input
                type="text"
                value={editForm.excerpt}
                onChange={(e) => setEditForm(prev => ({ ...prev, excerpt: e.target.value }))}
                className="input-field w-full"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                <select
                  value={editForm.scope}
                  onChange={(e) => setEditForm(prev => ({ ...prev, scope: e.target.value as any }))}
                  className="input-field w-full"
                >
                  <option value="global">Global</option>
                  <option value="internal">Internal (Staff Only)</option>
                  <option value="client_specific">Client-Specific</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value as any }))}
                  className="input-field w-full"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Article Content - View Mode */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {article.excerpt && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-primary-500">
              <p className="text-gray-700 italic">{article.excerpt}</p>
            </div>
          )}
          
          <div className="prose prose-lg max-w-none">
            <div
              className="text-gray-700"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(article.content || '') }}
            />
          </div>
        </div>
      )}

      {/* Article Rating */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Was this article helpful?</h3>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => handleRate(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <HandThumbUpIcon className="h-4 w-4 mr-2" />
            Yes ({article.helpfulCount})
          </button>
          <button
            onClick={() => handleRate(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <HandThumbDownIcon className="h-4 w-4 mr-2" />
            No ({article.notHelpfulCount})
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Article</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "{article.title}"? This action cannot be undone.
              </p>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-primary bg-red-600 hover:bg-red-700"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ArticleDetail
